<template>
  <div class="order-info">
    <BaseElForm v-if="order" label-width="150px" label-position="left">
      <!-- <p class="section-title text-primary-100 font-medium">{{ index }}.預約訂單</p> -->
      <BaseElFormItem v-if="!readonly && checkAction('admin.appointmentOrder.updateInfo')" :label="$t('common.table.action.text')">
        <BaseElButton class="primary-btn underline" type="text" :disabled="loading" @click="showEditDialog = true">
          {{ $t('orderInfoBlock.button.editOrder.text') }}
        </BaseElButton>
      </BaseElFormItem>
      <BaseElFormItem :label="$t('orderInfoBlock.code.title')">
        <div>
          <span class="mr-3">{{ order.code }}</span>
          <BaseElButton
            v-if="showControl.btn.viewAccessControl"
            type="text"
            class="table-opt-edit"
            @click="$router.push({
              name: 'MemberProfile',
              params: { id: order.MemberId },
              query: { tab: 'accessControlRecord' }
            })"
          >
            檢視門禁
          </BaseElButton>
          <BaseElButton
            v-if="showControl.btn.viewAguardAccessControl"
            type="text"
            class="table-opt-edit"
            @click="showAguardDialog = true"
          >
            檢視門禁
          </BaseElButton>
        </div>
      </BaseElFormItem>
      <BaseElFormItem :label="$t('orderInfoBlock.totalPrice.title')">
        <div>{{ displayCurrencySymbol }} {{ getData(order, 'AppointmentOrder.totalPrice', '') }}</div>
      </BaseElFormItem>
      <BaseElFormItem :label="$t('orderInfoBlock.recognizedAmount.title')">
        <div>{{ displayCurrencySymbol }} {{ getData(order, 'AppointmentOrder.recognizedAmount', '') }}</div>
      </BaseElFormItem>
      <BaseElFormItem :label="$t('orderInfoBlock.status.title')">
        <Tag
          v-if="getData(order, 'AppointmentOrder.status', '')"
          style="display: inline-flex"
          :type="orderStatus(getData(order, 'AppointmentOrder.status'), 'tag')"
          disable-transitions
        >
          {{ $t(orderStatus(getData(order, 'AppointmentOrder.status'), 'name')) }}
          <!-- {{order.status}} -->
        </Tag>
      </BaseElFormItem>

      <BaseElFormItem :label="$t('orderInfoBlock.memberType.title')">
        <div>
          <span class="mr-3">{{ memberType(order) }}</span>
          <BaseElButton
            v-if="order.Member && $route.name !== 'MemberProfile'"
            type="text"
            class="table-opt-edit"
            @click="$router.push(`/members/member-profile/${order.MemberId}`)"
          >
            {{ $t('orderInfoBlock.button.goProfile.text') }}
          </BaseElButton>
        </div>
      </BaseElFormItem>

      <BaseElFormItem :label="$t('orderInfoBlock.peopleCount.title')">
        <div>{{ peopleCount }}</div>
      </BaseElFormItem>
      <BaseElFormItem v-if="isPubApt" :label="$t('orderInfoBlock.user.email.title')">
        <div>{{ displayData.email }}</div>
      </BaseElFormItem>
      <BaseElFormItem :label="$t('orderInfoBlock.user.name.title')">
        <div>{{ displayData.name }}</div>
      </BaseElFormItem>
      <BaseElFormItem v-if="showControl.field.cyjPosBinding && order.Member" :label="$t('orderInfoBlock.user.cyjPosBinding')">
        <div v-if="cyjPosCsid">{{ cyjPosCsid }}</div>
        <BaseElButton
          v-else-if="showControl.btn.syncCyjPos"
          type="text"
          class="primary-btn underline"
          :loading="syncCyjPosLoading"
          @click="syncCyjPosMember"
        >
          {{ $t('orderInfoBlock.button.syncToPos') }}
        </BaseElButton>
        <span v-else>-</span>
      </BaseElFormItem>
      <BaseElFormItem :label="$t('orderInfoBlock.user.birthday.title')">
        <div>{{ displayData.birthday }}</div>
      </BaseElFormItem>
      <BaseElFormItem :label="$t('orderInfoBlock.user.phone.title')">
        <div>{{ displayData.phone }}</div>
      </BaseElFormItem>
      <BaseElFormItem v-if="isPubApt" :label="$t('orderInfoBlock.user.gender.title')">
        <div>{{ displayData.gender }}</div>
      </BaseElFormItem>
      <BaseElFormItem v-if="useMemberAddress" :label="$t('orderInfoBlock.address.title')">
        <div>{{ displayData.address }}</div>
      </BaseElFormItem>

      <BaseElFormItem :label="$t('orderInfoBlock.comment.title')">
        <div style="width: 100%; word-wrap: break-word">
          {{ displayData.comment || $t('common.none.text') }}
        </div>
      </BaseElFormItem>
      <BaseElFormItem :label="$t('orderInfoBlock.shopNote.title')">
        <div>{{ displayData.shopNote || $t('common.none.text') }}</div>
      </BaseElFormItem>
      <!-- 所有店家都顯示：推薦加購的加購意向寫在系統備註（CNCY-10770） -->
      <BaseElFormItem label="系統備註">
        <div style="width: 100%; white-space: pre-line; word-wrap: break-word">{{ displayData.systemComment || $t('common.none.text') }}</div>
      </BaseElFormItem>
      <BaseElFormItem :label="$t('orderInfoBlock.origin.title')">
        <div>{{ reservationOrigins }}</div>
      </BaseElFormItem>
    </BaseElForm>

    <EditReservationStatusDialog
      v-if="!readonly && showEditDialog"
      :data="order"
      @close="showEditDialog = false"
      @refresh="$emit('refresh')"
    />
    <AguardAccessControlDialog
      v-if="showAguardDialog"
      :order="order"
      @close="showAguardDialog = false"
    />
  </div>
</template>

<script>
import { computed, ref } from 'vue'
import EditReservationStatusDialog from '@/components/Reservation/EditReservationDialog/EditReservationStatusDialog.vue'
import AguardAccessControlDialog from './AguardAccessControlDialog.vue'
import { reservationOrderOrigins } from '@/utils/reservation'
import { get } from 'lodash'
import { getData } from '@/utils/object'
import { orderStatusConfig } from '@/config/reservation'
import { checkUserFeature } from '@/store/modules/permission'
import { mapGetters } from 'vuex'
import { formatDate } from '@/utils/date'
import { genderName } from '@/config/userInfoOptions'
import { i18n } from '@/plugins/i18n/i18n'
import { useCurrency } from '@/use/useCurrency'
import { usePermissions } from '@/use/permissions'
import { useShop } from '@/use/shop'
import { SyncCyjPosMemberImmediately } from '@/api/cyjPos'

export default {
  name: 'ReservationOrderInfoBlock',
  components: {
    EditReservationStatusDialog,
    AguardAccessControlDialog,
  },
  props: {
    loading: Boolean,
    order: {
      type: Object,
      default: () => ({}),
    },
    index: Number,
    readonly: Boolean,
  },
  setup (props, { emit }) {
    const { displayCurrencySymbol } = useCurrency()
    const { checkAction } = usePermissions()
    const { shopId } = useShop()
    const isPubApt = computed(() => get(props.order, 'AppointmentOrder.origin') === 'pubApt')
    const displayData = computed(() => {
      const order = props.order
      const AppointmentVisitorPreOrder = get(order, 'AppointmentOrder.AppointmentVisitorPreOrder')
      const name = isPubApt.value
        ? `${get(AppointmentVisitorPreOrder, 'profile.firstName')} ${get(
          AppointmentVisitorPreOrder,
          'profile.lastName',
        )}`
        : get(order, 'AppointmentOrder.userName', '-')
      const birthday = isPubApt.value
        ? get(AppointmentVisitorPreOrder, 'profile.birthDate')
        : get(order, 'Member.UserInfo.birthday')
      const phone = isPubApt.value
        ? `${get(AppointmentVisitorPreOrder, 'profile.phoneCountryCode')} ${get(
          AppointmentVisitorPreOrder,
          'profile.phoneNumber',
        )}`
        : get(order, 'AppointmentOrder.userPhone', '-')
      const address = get(order, 'AppointmentOrder.userAddress', '-')
      const comment = get(order, 'AppointmentOrder.userComment', '無')
      const systemComment = get(order, 'AppointmentOrder.systemComment')
      const shopNote = get(order, 'AppointmentOrder.adminComment', '無')
      const email = isPubApt.value
        ? get(AppointmentVisitorPreOrder, 'Visitor.email', '-')
        : undefined
      const gender = isPubApt.value
        ? get(genderName, get(AppointmentVisitorPreOrder, 'profile.gender', '-'))
        : undefined
      return {
        name,
        birthday: birthday ? formatDate(birthday, 'YYYY/MM/DD') : '-',
        phone,
        address,
        comment,
        systemComment,
        shopNote,
        email,
        gender,
      }
    })
    const showControl = computed(() => ({
      field: {
        cyjPosBinding: checkAction('planModule.cyjPos.enable'),
      },
      btn: {
        viewAccessControl: checkAction('admin.miezoAccessControlPassword.find') &&
          !!get(props.order, 'AppointmentOrder.customInfo.miezoAccessControlPassword'),
        viewAguardAccessControl: !!get(props.order, 'AppointmentOrder.customInfo.aguardAccessControl'),
        syncCyjPos: checkAction('admin.shopCustomModule.syncCyjPosMemberImmediately'),
      },
    }))
    const chargeType = computed(() => {
      return get(props.order, 'AppointmentOrder.chargeType')
    })
    const cyjPosCsid = computed(() => get(props.order, 'Member.UserInfo.systemCustom.cyjPosCsid'))
    const syncCyjPosLoading = ref(false)
    const syncCyjPosMember = async () => {
      const memberId = get(props.order, 'MemberId')
      if (!memberId) return
      syncCyjPosLoading.value = true
      try {
        const [, err] = await SyncCyjPosMemberImmediately({
          shopId: shopId.value,
          memberId,
        })
        if (err) {
          window.$message.error(err)
          return
        }
        window.$message.success(i18n.t('member.memberProfile.syncSuccess'))
        emit('refresh')
      } finally {
        syncCyjPosLoading.value = false
      }
    }
    const memberType = (order) => {
      if (order.Member) {
        return i18n.t('orderInfoBlock.memberType.member.title')
      } else if (isPubApt.value) {
        return i18n.t('orderInfoBlock.memberType.isPubApt.title')
      } else {
        return i18n.t('orderInfoBlock.memberType.nonMember.title')
      }
    }
    return { showControl, displayData, isPubApt, memberType, chargeType, displayCurrencySymbol, cyjPosCsid, syncCyjPosLoading, syncCyjPosMember }
  },
  data: () => ({
    showEditDialog: false,
    showAguardDialog: false,
  }),
  computed: {
    ...mapGetters(['shop', 'userPlanLimit', 'userFeatures', 'userPlanFeature']),
    isCancel () {
      return getData(this.order, 'AppointmentOrder.status') === 'cancel'
    },
    peopleCount () {
      return get(this.order, 'AppointmentOrder.peopleCount', 1)
    },
    reservationOrigins () {
      const notSpecify = this.order.fromNotSpecify
      const origin = get(this.order, 'AppointmentOrder.origin', '')
      const originText = get(this.order, 'AppointmentOrder.originText', '')
      return reservationOrderOrigins({ notSpecify, origin, originText })
    },
    useMemberAddress () {
      return this.checkAction('adminView.member.findUserInfoMoreAddress')
    },
  },
  methods: {
    getData,
    checkAction (action) {
      return checkUserFeature(this.userPlanFeature, this.userFeatures, action)
    },
    orderStatus (status, attr) {
      return get(orderStatusConfig[status], attr)
    },
    statusContent (status) {
      const statusDict = {
        cancel: '已取消',
        wait: '已預約',
        complete: '完　成',
        absence: '未　到',
      }
      return statusDict[status] || 'N/A'
    },
  },
}
</script>

<style scoped lang="scss"></style>
